import { Page } from '@/components';
import { Box, Flex, Heading } from '@blockanalitica/ui';
import styled, { css } from 'styled-components';
import GlossaryLine from './GlossaryLine';
import RestrictedSection from './RestrictedSection';

const NumberedList = styled.ol`
  list-style-position: inside;
  list-style-type: upper-roman;

  ${(props) => css`
    padding-left: ${props.theme.space['xs']};
    margin-top: ${props.theme.space['3xs']};
    margin-bottom: ${props.theme.space['3xs']};
    marign: 0;
  `}
`;

export default function GlossaryPage() {
  return (
    <Page title={<Heading size="1">Glossary</Heading>}>
      <Box as="p" color="muted">
        Definitions are simplified and may not be comprehensive.
      </Box>
      <Flex gap="s" flexDirection="column" justifyContent="space-between">
        <GlossaryLine title="Total Supply">
          The combined total supply of DAI and USDS.
        </GlossaryLine>
        <GlossaryLine title="Surplus">
          A system-level surplus received primarily from Stability Fee revenue
          and Liquidation revenue.
        </GlossaryLine>
        <GlossaryLine title="Collateralization">
          The total dollar-denominated value of collateral in the system used to
          borrow DAI and USDS.
        </GlossaryLine>
        <GlossaryLine title="Smart Burn Engine">
          A smart contract system designed to allocate excess system surplus
          towards either:
          <NumberedList>
            <li>Accumulating SKY</li>
            <li>
              Accumulating SKY and redepositing the tokens alongside a
              proportional amount of USDS to an AMM liquidity pool..
            </li>
          </NumberedList>
        </GlossaryLine>
        <GlossaryLine title="Estimated Annual Profit">
          How much Net Revenue the system will make assuming that protocol
          parameters, such as stability fees, liquidation penalties, PSM fees,
          and expenses remain constant.
        </GlossaryLine>
        <GlossaryLine title="USDS">
          SkyDollar - a USD stablecoin (upgraded version of the DAI token).
        </GlossaryLine>
        <GlossaryLine title="SKY">
          The governance token of the Sky Ecosystem and SKY Protocol. An
          upgraded version of MKR. Redenominated 1:24000 by MKR and
          bidirectionally convertible.
        </GlossaryLine>
        <GlossaryLine title="DAI">
          The Dai Stablecoin, which is a USD stablecoin.
        </GlossaryLine>
        <GlossaryLine title="MKR">The MakerDAO governance token.</GlossaryLine>
        <GlossaryLine title="Total Liquidations">
          The number of liquidations that have been executed since inception.
        </GlossaryLine>
        <GlossaryLine title="Total Collateral Auctioned">
          The total dollar-denominated amount of collateral which has been
          auctioned off during collateral auctions.
        </GlossaryLine>
        <GlossaryLine title="Total Debt Repaid">
          The total dollar-denominated amount of debt which has been
          successfully repaid during collateral auctions.
        </GlossaryLine>
        <GlossaryLine title="Total DAI Supply">
          This is calculated as the total system debt (vat.debt) minus total
          USDS supply, unborrowed DAI supplied by Sky in Spark, idle DAI in the
          Spark DAI Vault via Morpho, premint DAI in LitePSM and surplus buffer.
        </GlossaryLine>
        <GlossaryLine title="Total USDS Supply">
          This is calculated as the total USDS supply (ERC20 token) + surplus
          buffer.
        </GlossaryLine>

        <GlossaryLine title="USDS Diversification">
          Debt backed by specific collateral types in the system.
        </GlossaryLine>
        <Heading size="2" paddingTop="m" paddingBottom="2xs">
          Seal Engine
        </Heading>
        <GlossaryLine title="Seal Engine">
          The main objective of the Seal Engine is to incentivise long-term
          governance participation by enabling MKR holders to Seal their tokens.
          By sealing MKR, a number of use cases are enabled: (i) delegation of
          voting power, (ii) Seal Rewards, and (iii) USDS borrowing.
        </GlossaryLine>
        <GlossaryLine title="Delegation of Voting Power">
          Seal Engine users can delegate their MKR voting power to delegates in
          Sky Governance.{' '}
        </GlossaryLine>
        <GlossaryLine title="Seal to Borrow USDS">
          Users who Seal their MKR can borrow USDS against the Sealed principal
          token amount, with risk parameters set conservatively through Sky’s
          decentralized governance process. This borrowing mechanism functions
          similarly to a Native Vault.
        </GlossaryLine>
        <GlossaryLine title="Exit Fee">
          Unsealing MKR requires an exit fee payment. The exit fee does not
          affect the accumulated rewards. It is only applied to the principal
          locked amount. The paid exit fee (MRK) is burnt and ultimately removed
          from circulation.
        </GlossaryLine>
        <GlossaryLine title="Seal Engine Parameters:">
          Seal Engine parameters are set through Sky’s decentralized governance
          process. Seal Engine parameters determine USDS borrowing constraints,
          including (i) debt ceiling, (ii) stability fee, (iii) liquidation
          ratio, and (iv) auction outcomes.
        </GlossaryLine>

        <Heading size="2" paddingTop="m" paddingBottom="2xs">
          Smart Burn Engine
        </Heading>
        <GlossaryLine title="Liquidity Owned">
          The amount of dollar-denominated liquidity owned by the MCD Pause
          Proxy.
        </GlossaryLine>
        <GlossaryLine title="MKR Owned">
          The amount of MKR owned by the MCD Pause Proxy.
        </GlossaryLine>
        <GlossaryLine title="DAI Owned">
          The amount of DAI owned by the MCD Pause Proxy.
        </GlossaryLine>
        <GlossaryLine title="SKY Owned">
          The amount of SKY tokens owned by the Sky Protocol through the Smart
          Burn Engine.
        </GlossaryLine>

        <Heading size="2" paddingTop="m" paddingBottom="2xs">
          Maker Core Parameters:
        </Heading>
        <GlossaryLine title="Debt Ceiling">
          Sets the maximum amount of DAI minting for a specific collateral type.
        </GlossaryLine>
        <GlossaryLine title="DC-IAM Line">
          The maximum value for the Debt Ceiling that the Debt Ceiling Instant
          Access Module (DC-IAM) will allow in a given vault type.
        </GlossaryLine>
        <GlossaryLine title="DC-IAM Gap">
          How much of a gap the DC-IAM aims to maintain between the current debt
          usage and the Debt Ceiling of a vault type.
        </GlossaryLine>
        <GlossaryLine title="DC-IAM ttl">
          How frequently the Debt Ceiling can be increased by the DC-IAM.
        </GlossaryLine>
        <GlossaryLine title="Liquidation Ratio (LR)">
          Limits the maximum amount of DAI debt that a vault user can draw from
          their vault given the value of their collateral locked in that vault.
        </GlossaryLine>
        <GlossaryLine title="Collateral Locked">
          The dollar-denominated value of tokens locked in a particular vault
          for the use as collateral.
        </GlossaryLine>
        <GlossaryLine title="Stability Fee">
          The annual percentage fee charged on the DAI generated on Vaults.
        </GlossaryLine>
        <GlossaryLine title="OSM Price">
          The price currently issued by the Oracle Security Module price feed.
        </GlossaryLine>
        <GlossaryLine title="Next OSM Price">
          The OSM Price enacts a one-hour delay. It is updated every hour.
        </GlossaryLine>
        {/*
        <GlossaryLine title="Next OSM Price Change">
          Time until the OSM Price is updated.
        </GlossaryLine> */}
        <GlossaryLine title="Dust">
          The Debt Floor Parameter. Sets the minimum amount of DAI that can be
          borrowed from an individual vault.
        </GlossaryLine>
        <GlossaryLine title="Chop">
          Controls the fee vault owners must pay when their position is
          liquidated due to insufficient collateral.
        </GlossaryLine>
        <GlossaryLine title="Hole">
          Sets the maximum amount of DAI debt for which collateral auctions can
          be active at any one time within a particular vault type.
        </GlossaryLine>
        <GlossaryLine title="Buf">
          A multiplier that is applied to the starting price of a collateral
          auction.
        </GlossaryLine>
        <GlossaryLine title="Tail">
          Sets the maximum time that can elapse before an auction needs to reset
          for a particular vault type.
        </GlossaryLine>
        <GlossaryLine title="Cusp">
          The maximum percentage drop in collateral price during a collateral
          auction before the auction is reset.
        </GlossaryLine>
        <GlossaryLine title="Chip">
          Represents a reward in DAI paid to the Keepers that triggers
          collateral liquidation auctions.
        </GlossaryLine>
        <GlossaryLine title="Tip">
          The rewards in DAI paid to the Keepers that trigger collateral
          liquidation auctions.
        </GlossaryLine>
        <GlossaryLine title="Step">
          The length of time between price drops during a collateral auction.
        </GlossaryLine>
        <GlossaryLine title="Cut">
          The ‘depth’ of each step in the Auction Price Function that determines
          how the collateral price changes over time during a collateral
          auction.
        </GlossaryLine>
        <Heading size="2" paddingTop="m" paddingBottom="2xs">
          SkyLink:
        </Heading>
        <GlossaryLine title="SkyLink">
          SkyLink serves as the official bridge of the Sky Ecosystem, extending
          its core features to other L2s and blockchains. For the first
          iteration, SkyLink supports the bridging of native USDS and sUSDS from
          the Sky Protocol on Ethereum Mainnet to Base.{' '}
        </GlossaryLine>

        <GlossaryLine title="Base">
          Base is an Ethereum Layer 2 (L2) chain developed and maintained by
          Coinbase.
        </GlossaryLine>

        <GlossaryLine title="SkyLink <> Base - SkyLink Bridge Activity">
          The SkyLink Bridge Activity page records USDS and sUSDS bridge events
          to and from Base.
        </GlossaryLine>

        <GlossaryLine title="SkyLink <> Base - Bridged USDS: ">
          The total amount of USDS that is currently bridged to Base.
        </GlossaryLine>

        <GlossaryLine title="SkyLink <> Base - Bridged sUSDS: ">
          The total amount of sUSDS that is currently bridged to Base.
        </GlossaryLine>

        <GlossaryLine title="SkyLink <> Base - Total Assets Bridged">
          The combined amount of USDS and sUSDS currently bridged to Base.
        </GlossaryLine>

        <Heading size="2" paddingTop="m" paddingBottom="2xs">
          Spark Liquidity Layer
        </Heading>

        <GlossaryLine title="Spark Liquidity Layer (SLL)">
          The SLL automates provision of USDS, sUSDS and USDC from the Sky
          Protocol on Ethereum Mainnet across various blockchain networks and
          DeFi protocols. The SLL uses SkyLink to bridge USDS and sUSDS. To
          bridge USDC, the SLL uses Circle’s Cross-Chain Transfer Protocol
          (CCTP).
        </GlossaryLine>
        {/* Redacted until we're showing data regarding the CCTP */}
        {/* <GlossaryLine title="Cross-Chain Transfer Protocol (CCTP)">
          Developed by Circle, CCTP is an on-chain utility that facilitates USDC
          transfers securely between blockchains via native burning and minting
        </GlossaryLine> */}

        <GlossaryLine title="PSM3">
          For the first iteration of SLL, USDS, sUSDS, and USDC are bridged from
          the Sky Protocol on Ethereum Mainnet to a three-way Peg Stability
          Module (PSM3) on Base. The PSM3 enables users on Base to access the
          Sky Savings Rate by swapping USDS or USDC for sUSDS.
        </GlossaryLine>

        <GlossaryLine title="PSM3 <> Base - USDS Balance">
          The total amount of USDS that is currently in the PSM3 on Base.
        </GlossaryLine>

        <GlossaryLine title="PSM3 <> Base - sUSDS Balance">
          The total amount of sUSDS that is currently in the PSM3 on Base.
        </GlossaryLine>

        <GlossaryLine title="PSM3 <> Base - USDC Balance">
          The total amount of USDC that is currently in the PSM3 on Base.
        </GlossaryLine>

        <GlossaryLine title="PSM3 <> Base - Total Assets">
          The total dollar-denominated value currently in the PSM3 on Base.
        </GlossaryLine>

        <GlossaryLine title="PSM3 <> Base - Swap Activity">
          The PSM3 Swap Activity page records USDS, sUSDS, and USDC swap events
          in and out of the PSM3 on Base{' '}
        </GlossaryLine>

        <RestrictedSection />
      </Flex>
    </Page>
  );
}
